import { useEffect } from 'react';
import { Image } from '@rocco/components/image';
import { RoccoLink } from '@rocco/components/link';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { usePrefersReducedMotion } from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { FadingLogomark } from '~/ui-rocco/pages/products-page/FadingLogomark';
import type { EditorialHeroArticle } from './ArticlesDirectoryRevamp';

const ADVANCE_MS = 6000;

/**
 * Auto-advance, inside the Carousel so `useCarousel` resolves. Renders
 * nothing: the strip carries no visible controls — a swipe is the control —
 * so the first touch on the strip also ends the auto-advance, rather than
 * scrolling the reader off the slide they chose six seconds later. It stays
 * put entirely for a reader who prefers reduced motion or a single slide.
 */
const HeroAutoplay = ({ total }: { total: number }) => {
    const { api } = useCarousel();
    const prefersReducedMotion = usePrefersReducedMotion();

    useEffect(() => {
        if (!api || prefersReducedMotion || total < 2) return;
        const id = setInterval(() => api.scrollNext(), ADVANCE_MS);
        const stop = () => clearInterval(id);
        api.on('pointerDown', stop);
        // A keyboard reader chooses a slide by focusing its link, which
        // fires no pointer event — that engagement ends the auto-advance
        // the same way a touch does.
        const root = api.rootNode();
        root.addEventListener('focusin', stop);
        return () => {
            clearInterval(id);
            api.off('pointerDown', stop);
            root.removeEventListener('focusin', stop);
        };
    }, [api, prefersReducedMotion, total]);

    return null;
};

/**
 * The phone masthead for /articles: the featured-article carousel as a
 * full-bleed hero under the transparent header, the way the project detail
 * page opens. Mounted only when the route hands it slides, which it does for
 * mobile devices with the revamp on — the desktop composition is untouched.
 *
 * Scrim and tint are the drawn masthead's (EditorialDirectoryHero): a flat
 * lift so an arbitrary bright photograph cannot wash the headline out, and
 * the gradient the design draws behind the text.
 */
export const EditorialDirectoryMobileHero = ({
    articles,
}: {
    articles: EditorialHeroArticle[];
}) => {
    if (articles.length === 0) return null;

    return (
        <section
            aria-label="Featured articles"
            className="relative ml-[calc(50%-50vw)] w-screen overflow-hidden bg-[#131313]"
        >
            {/* The directory's A mark, where /products and /projects set
                theirs — a gutter in, 2.375rem under the header — white over
                the photograph and fading as the page scrolls it up to the
                header. Isolated, so the mark's own back layer stays above
                the picture rather than behind the section's ground. */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute top-[7.125rem] left-4 isolate z-10 md:hidden"
            >
                <FadingLogomark tone="dark" />
            </div>
            <Carousel options={{ loop: true }}>
                <CarouselContent>
                    {articles.map((article, index) => (
                        <CarouselItem
                            key={article.link}
                            className="max-w-full basis-full"
                        >
                            {/* Capped for landscape: the portrait ratio alone
                                would run past the viewport once the phone
                                turns. */}
                            <RoccoLink
                                to={article.link}
                                className="relative block aspect-[414/560] max-h-[80svh] w-full"
                            >
                                {article.image?.src && (
                                    <Image
                                        src={article.image.src}
                                        alt=""
                                        fill={true}
                                        fit="cover"
                                        loading={index === 0 ? 'eager' : 'lazy'}
                                        className="absolute inset-0"
                                    />
                                )}
                                <div
                                    aria-hidden={true}
                                    className="pointer-events-none absolute inset-0 bg-[#131313]/[0.22]"
                                />
                                <div
                                    aria-hidden={true}
                                    className="pointer-events-none absolute inset-x-0 bottom-0 h-[24rem] bg-gradient-to-t from-[#131313]/[0.89] from-[6.207%] to-transparent to-[70.169%]"
                                />
                                <div className="absolute inset-x-0 bottom-0 flex flex-col gap-[0.75rem] px-[1rem] pb-[2rem]">
                                    {article.date && (
                                        <p className="m-0 font-sans text-[0.875rem] leading-[1.25] font-medium text-white">
                                            ({article.date})
                                        </p>
                                    )}
                                    <h2 className="m-0 line-clamp-3 font-gestura text-[2.25rem] leading-[2.5rem] font-normal tracking-[-0.09rem] text-[#e5e5e5]">
                                        {article.title}
                                    </h2>
                                    {(article.label ||
                                        article.categoryLabel) && (
                                        <p className="m-0 font-sans text-[0.875rem] leading-[1.25] font-medium text-white/90">
                                            {article.label}
                                            {article.label &&
                                            article.categoryLabel
                                                ? ' in: '
                                                : ''}
                                            {article.categoryLabel}
                                        </p>
                                    )}
                                </div>
                            </RoccoLink>
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <HeroAutoplay total={articles.length} />
            </Carousel>
        </section>
    );
};
